import React, { useState } from 'react';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import './Navbar.css';

function Navbar({ currentPage, onNavigateToSection, onSelectCourse }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    if (onNavigateToSection) {
      onNavigateToSection(sectionId);
    }
  };

  const handleProgramClick = (programObj) => {
    setActiveSection('courses');
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    if (onSelectCourse) {
      onSelectCourse(programObj);
    } else if (onNavigateToSection) {
      onNavigateToSection('courses');
    }
  };

  const isNavActive = (sectionId) => {
    if (currentPage === 'about') {
      return sectionId === 'about';
    }
    if (currentPage === 'founder') {
      return sectionId === 'founder';
    }
    if (currentPage === 'contact') {
      return sectionId === 'contact';
    }
    if (currentPage === 'course-details') {
      return sectionId === 'courses';
    }
    return activeSection === sectionId;
  };

  return (
    <nav className="navbar">
      <div className="container navbar-container">
        <button 
          onClick={() => handleNavClick('home')} 
          className="navbar-brand-btn"
        >
          <img src="/images/Logo.png" alt="Logo" className="navbar-logo-img" />
        </button>

        <ul className={`nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <li>
            <button 
              className={`nav-link ${isNavActive('home') ? 'active' : ''}`}
              onClick={() => handleNavClick('home')}
            >
              Home
            </button>
          </li>
          <li>
            <button 
              className={`nav-link ${isNavActive('about') ? 'active' : ''}`}
              onClick={() => handleNavClick('about')}
            >
              About
            </button>
          </li>

          {/* ================= PROGRAMS MEGA DROPDOWN ================= */}
          <li 
            className={`nav-item-dropdown ${dropdownOpen ? 'dropdown-open' : ''}`}
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button 
              className={`nav-link ${isNavActive('courses') ? 'active' : ''}`}
              onClick={() => handleNavClick('courses')}
            >
              Programs
              <ChevronDown size={14} className="dropdown-arrow-icon" />
            </button>

            <div className={`programs-mega-dropdown ${dropdownOpen ? 'open' : ''}`}>
              <div className="mega-dropdown-inner">
                
                {/* COLUMN 1: SCHOOL STUDENTS */}
                <div className="mega-column">
                  <h4 className="mega-category-title">SCHOOL STUDENTS</h4>
                  <div className="mega-links-list">
                    <div 
                      className="mega-item" 
                      onClick={() => handleProgramClick({
                        id: 1,
                        title: "Beyond Marks",
                        target: "Designed for: Class 7–8 Students",
                        level: "Class 7–8",
                        category: "School Students",
                        desc: "Beyond Marks helps Class 7–8 students discover their strengths, interests, personality and aspirations—building self-awareness and clarity for their future education and career choices.",
                        image: "/images/course_banner_detail.jpg"
                      })}
                    >
                      <span className="mega-item-name">Beyond Marks</span>
                      <span className="mega-item-target">Class 7–8</span>
                    </div>
                    <div 
                      className="mega-item" 
                      onClick={() => handleProgramClick({
                        id: 2,
                        title: "Future Pathways",
                        target: "Designed for: Class 9–10 Students",
                        level: "Class 9–10",
                        category: "School Students",
                        desc: "Future Pathways helps Class 9–10 students understand their strengths, interests and aspirations, explore suitable career directions, and build clarity before making important stream and subject choices.",
                        image: "/images/Home/Course2.jpeg"
                      })}
                    >
                      <span className="mega-item-name">Future Pathways</span>
                      <span className="mega-item-target">Class 9–10</span>
                    </div>
                    <div 
                      className="mega-item" 
                      onClick={() => handleProgramClick({
                        id: 3,
                        slug: "global-pathways",
                        title: "Global Pathways",
                        target: "Designed for: Class 9–11 Students Planning a Bachelor's Degree Abroad",
                        level: "Class 9–11",
                        category: "School Students",
                        desc: "Global Pathways helps students explore undergraduate study options abroad with greater clarity. It connects self-discovery, career direction and future goals with the right higher education pathways, helping students make informed decisions about countries, courses and universities.",
                        image: "/images/Home/Course3.jpeg"
                      })}
                    >
                      <span className="mega-item-name">Global Pathways</span>
                      <span className="mega-item-target">Class 9–11</span>
                    </div>
                    <div 
                      className="mega-item" 
                      onClick={() => handleProgramClick({
                        id: 4,
                        slug: "career-discovery",
                        title: "Career Discovery",
                        target: "Designed for: Class 11–12 Students",
                        level: "Class 11–12",
                        category: "School Students",
                        desc: "Career Discovery helps Class 11–12 students understand themselves, explore right-fit career pathways and make confident decisions about their future. Through structured self-discovery, scientific assessment and guided career research, students build clarity before choosing their next step.",
                        image: "/images/Home/Course3.jpeg"
                      })}
                    >
                      <span className="mega-item-name">Career Discovery</span>
                      <span className="mega-item-target">Class 11–12</span>
                    </div>
                  </div>
                </div>

                {/* COLUMN 2: HIGHER EDUCATION & WORKING PROFESSIONALS */}
                <div className="mega-column">
                  {/* HIGHER EDUCATION */}
                  <div className="mega-section-block">
                    <h4 className="mega-category-title">HIGHER EDUCATION</h4>
                    <div className="mega-links-list">
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 5,
                          slug: "career-launch",
                          title: "Career Launch",
                          target: "Designed for: Undergraduate Students",
                          level: "Undergraduate",
                          category: "Higher Education",
                          desc: "Career Launch helps undergraduate students understand their strengths, interests and career goals, explore suitable career pathways and prepare for a confident transition from college to the world of work.",
                          image: "/images/Home/Course4.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Career Launch</span>
                        <span className="mega-item-target">Undergraduate</span>
                      </div>
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 6,
                          slug: "global-pathways-postgrad",
                          title: "Global Pathways",
                          target: "Designed for: Undergraduate Students Planning a Master’s Abroad",
                          level: "Master’s Abroad",
                          category: "Higher Education",
                          desc: "Global Pathways helps undergraduate students understand their strengths and career goals, explore suitable Master’s pathways abroad, and make informed decisions about higher education and their future.",
                          image: "/images/Home/Course6.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Global Pathways</span>
                        <span className="mega-item-target">Master’s Abroad</span>
                      </div>
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 7,
                          slug: "career-acceleration",
                          title: "Career Acceleration",
                          target: "Designed for: Postgraduate Students",
                          level: "Postgraduate Students",
                          category: "Higher Education",
                          desc: "Career Acceleration helps postgraduate students gain clarity on their career direction, understand their strengths and interests, and make informed decisions about their next professional step.",
                          image: "/images/Home/Course5.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Career Acceleration</span>
                        <span className="mega-item-target">Postgraduate Students</span>
                      </div>
                    </div>
                  </div>

                  {/* WORKING PROFESSIONALS */}
                  <div className="mega-section-block" style={{ marginTop: '28px' }}>
                    <h4 className="mega-category-title">WORKING PROFESSIONALS</h4>
                    <div className="mega-links-list">
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 8,
                          slug: "career-transition",
                          title: "Career Transition",
                          target: "Designed for: Young Working Professionals",
                          level: "Young Working Professionals",
                          category: "Working Professionals",
                          desc: "Career Transition helps young working professionals gain clarity about their strengths, interests and career goals while exploring suitable directions for their next professional chapter.",
                          image: "/images/Home/Course6.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Career Transition</span>
                        <span className="mega-item-target">Young Working Professionals</span>
                      </div>
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 9,
                          slug: "career-revival",
                          title: "Career Revival",
                          target: "Designed for: Unemployed Graduates",
                          level: "Unemployed Graduates",
                          category: "Working Professionals",
                          desc: "Career Revival helps unemployed graduates gain clarity about their strengths, interests and career direction while exploring suitable pathways for their next professional opportunity.",
                          image: "/images/Home/Course2.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Career Revival</span>
                        <span className="mega-item-target">Unemployed Graduates</span>
                      </div>
                      <div 
                        className="mega-item" 
                        onClick={() => handleProgramClick({
                          id: 10,
                          slug: "career-restart",
                          title: "Career Restart",
                          target: "Designed for: Unemployed Non-Graduates",
                          level: "Unemployed Non-Graduates",
                          category: "Working Professionals",
                          desc: "Career Restart helps unemployed non-graduates understand their strengths, interests and possibilities while exploring practical career directions and building a clear path toward their next opportunity.",
                          image: "/images/Home/Course1.jpeg"
                        })}
                      >
                        <span className="mega-item-name">Career Restart</span>
                        <span className="mega-item-target">Unemployed Non-Graduates</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </li>

          <li>
            <button 
              className={`nav-link ${isNavActive('founder') ? 'active' : ''}`}
              onClick={() => handleNavClick('founder')}
            >
              Founder
            </button>
          </li>
          <li>
            <button 
              className={`nav-link ${isNavActive('contact') ? 'active' : ''}`}
              onClick={() => handleNavClick('contact')}
            >
              Contact
            </button>
          </li>
        </ul>

        <div className="nav-actions">
          <button 
            className="btn-contact-nav"
            onClick={() => handleNavClick('contact')}
          >
            <span className="btn-contact-text">Book a Consultation</span>
            <span className="btn-contact-icon-circle">
              <ArrowRight size={16} />
            </span>
          </button>
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
